// Admin-Verwaltung der wiederkehrenden Mitarbeiter-Aufgaben pro Standort.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Plus, Pencil, Trash2, ClipboardList, Loader2, X, MapPin, CalendarClock, Bell, MessageCircle, Send, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const FREQ_LABEL = { daily: 'Täglich', weekly: 'Wöchentlich', monthly: 'Monatlich' };

const emptyForm = (studioId) => ({
  id: null,
  studio_id: studioId || '',
  title: '',
  description: '',
  frequency: 'monthly',
  due_day_of_month: 1,
  due_weekday: 0,
  is_active: true,
  reminders_enabled: true,
});

export const frequencyText = (t) => {
  if (t.frequency === 'daily') return 'Täglich';
  if (t.frequency === 'weekly') return `Wöchentlich · ab ${WEEKDAYS[t.due_weekday || 0]}`;
  return `Monatlich · ab dem ${t.due_day_of_month || 1}.`;
};

export const StaffTasksManageDialog = ({ open, onClose, studios = [], onChanged }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm(studios[0]?.id));
  const [tplStatus, setTplStatus] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff-tasks`);
      setTasks(res.data || []);
    } catch (e) {
      toast.error('Aufgaben konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTplStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff-tasks/whatsapp-template/status`);
      setTplStatus(res.data);
    } catch (e) {
      setTplStatus(null);
    }
  }, []);

  const createTpl = async () => {
    setTplBusy(true);
    try {
      const res = await axios.post(`${API}/staff-tasks/whatsapp-template/create`);
      if (res.data?.success) {
        toast.success('WhatsApp-Vorlage bei Meta eingereicht (Freigabe abwarten)');
      } else {
        toast.error('Vorlage konnte nicht erstellt werden');
      }
      await loadTplStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vorlage-Erstellung fehlgeschlagen');
    } finally {
      setTplBusy(false);
    }
  };

  const refreshTpl = async () => {
    setTplBusy(true);
    try {
      await loadTplStatus();
      toast.info('Status bei Meta abgefragt');
    } finally {
      setTplBusy(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      loadTplStatus();
      setForm(emptyForm(studios[0]?.id));
    }
  }, [open, load, loadTplStatus, studios]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.studio_id) { toast.error('Bitte einen Standort wählen'); return; }
    if (!form.title.trim()) { toast.error('Bitte einen Titel eingeben'); return; }
    setSaving(true);
    try {
      const payload = {
        studio_id: form.studio_id,
        title: form.title.trim(),
        description: form.description || '',
        frequency: form.frequency,
        due_day_of_month: Number(form.due_day_of_month) || 1,
        due_weekday: Number(form.due_weekday) || 0,
        is_active: form.is_active,
        reminders_enabled: form.reminders_enabled,
      };
      if (form.id) {
        await axios.put(`${API}/staff-tasks/${form.id}`, payload);
        toast.success('Aufgabe aktualisiert');
      } else {
        await axios.post(`${API}/staff-tasks`, payload);
        toast.success('Aufgabe erstellt');
      }
      setForm(emptyForm(studios[0]?.id));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const editTask = (t) => {
    setForm({
      id: t.id,
      studio_id: t.studio_id,
      title: t.title,
      description: t.description || '',
      frequency: t.frequency,
      due_day_of_month: t.due_day_of_month || 1,
      due_weekday: t.due_weekday || 0,
      is_active: t.is_active,
      reminders_enabled: t.reminders_enabled !== false,
    });
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Aufgabe „${t.title}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/staff-tasks/${t.id}`);
      toast.success('Aufgabe gelöscht');
      if (form.id === t.id) setForm(emptyForm(studios[0]?.id));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-tasks-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-gray-700" />
            Mitarbeiter-Aufgaben verwalten
          </DialogTitle>
          <DialogDescription>
            Wiederkehrende Aufgaben pro Standort. Sie erscheinen zuoberst im Terminkalender und
            bleiben rot, bis ein Trainer/Admin sie bestätigt.
          </DialogDescription>
        </DialogHeader>

        {/* WhatsApp-Vorlage Status */}
        {(() => {
          const st = tplStatus?.status || 'NOT_CREATED';
          const map = {
            APPROVED: ['Freigegeben', 'bg-green-100 text-green-700 border-green-300'],
            PENDING: ['In Prüfung bei Meta', 'bg-amber-100 text-amber-700 border-amber-300'],
            REJECTED: ['Abgelehnt', 'bg-red-100 text-red-700 border-red-300'],
            NOT_CREATED: ['Noch nicht erstellt', 'bg-gray-100 text-gray-600 border-gray-300'],
            NOT_FOUND: ['Nicht gefunden', 'bg-gray-100 text-gray-600 border-gray-300'],
          };
          const [label, cls] = map[st] || map.NOT_CREATED;
          return (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3" data-testid="wa-template-status-box">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  <span>WhatsApp-Vorlage:</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid="wa-template-status-badge">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {(st === 'NOT_CREATED' || st === 'NOT_FOUND' || st === 'REJECTED') ? (
                    <Button size="sm" variant="outline" onClick={createTpl} disabled={tplBusy} data-testid="wa-template-create-btn">
                      {tplBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      Bei Meta erstellen
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={refreshTpl} disabled={tplBusy} data-testid="wa-template-refresh-btn">
                      {tplBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                      Status aktualisieren
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Für proaktive WhatsApp-Erinnerungen ausserhalb des 24-Std.-Fensters ist eine von Meta
                freigegebene Vorlage nötig. Bis zur Freigabe wird als WhatsApp automatisch ein Freitext gesendet.
              </p>
            </div>
          );
        })()}

        {/* Formular */}
        <div className="rounded-lg border border-gray-200 p-4 space-y-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-gray-800">
              {form.id ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
            </h4>
            {form.id && (
              <button
                onClick={() => setForm(emptyForm(studios[0]?.id))}
                className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
                data-testid="staff-task-cancel-edit"
              >
                <X className="w-3 h-3" /> Neu
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Standort</Label>
              <Select value={form.studio_id} onValueChange={(v) => update({ studio_id: v })}>
                <SelectTrigger data-testid="staff-task-studio-select"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Häufigkeit</Label>
              <Select value={form.frequency} onValueChange={(v) => update({ frequency: v })}>
                <SelectTrigger data-testid="staff-task-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Täglich</SelectItem>
                  <SelectItem value="weekly">Wöchentlich</SelectItem>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Titel</Label>
            <Input
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="z.B. Küche putzen"
              data-testid="staff-task-title-input"
            />
          </div>

          <div>
            <Label className="text-xs">Beschreibung (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Details zur Aufgabe…"
              rows={2}
              data-testid="staff-task-description-input"
            />
          </div>

          {form.frequency === 'monthly' && (
            <div>
              <Label className="text-xs">Sichtbar/fällig ab Tag im Monat</Label>
              <Select value={String(form.due_day_of_month)} onValueChange={(v) => update({ due_day_of_month: Number(v) })}>
                <SelectTrigger data-testid="staff-task-dueday-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)} data-testid={`staff-task-dueday-option-${d}`}>{d}. des Monats</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.frequency === 'weekly' && (
            <div>
              <Label className="text-xs">Sichtbar/fällig ab Wochentag</Label>
              <Select value={String(form.due_weekday)} onValueChange={(v) => update({ due_weekday: Number(v) })}>
                <SelectTrigger data-testid="staff-task-weekday-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((w, i) => (
                    <SelectItem key={i} value={String(i)} data-testid={`staff-task-weekday-option-${i}`}>{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => update({ is_active: v })} data-testid="staff-task-active-switch" />
              <span className="text-sm text-gray-700">Aktiv</span>
            </div>
            <Button onClick={submit} disabled={saving} data-testid="staff-task-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : (form.id ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />)}
              {form.id ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-white border border-gray-200 p-2">
            <Switch checked={form.reminders_enabled} onCheckedChange={(v) => update({ reminders_enabled: v })} data-testid="staff-task-reminder-switch" />
            <div className="flex items-center gap-1.5">
              <Bell className="w-4 h-4 text-amber-500 mt-0.5" />
              <span className="text-xs text-gray-600">
                Erinnerung senden, wenn am Tag nach Fälligkeit noch offen
                <br />(WhatsApp + E-Mail an Standort-Team, plus Admin)
              </span>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="space-y-2">
          <h4 className="font-semibold text-sm text-gray-800 mt-2">Bestehende Aufgaben</h4>
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Noch keine Aufgaben angelegt.</p>
          ) : (
            tasks.map((t) => (
              <div
                key={t.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 p-3 bg-white"
                data-testid={`staff-task-item-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{t.title}</span>
                    {!t.is_active && <Badge variant="secondary" className="text-xs">Inaktiv</Badge>}
                  </div>
                  {t.description && <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.studio_name}</span>
                    <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />{frequencyText(t)}</span>
                    {t.reminders_enabled === false && (
                      <span className="flex items-center gap-1 text-gray-400"><Bell className="w-3 h-3" />Erinnerung aus</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => editTask(t)} data-testid={`staff-task-edit-${t.id}`}>
                    <Pencil className="w-4 h-4 text-gray-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeTask(t)} data-testid={`staff-task-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaffTasksManageDialog;
